import RestInterface from "./rest-interface";
import StoreInterface from "./store-interface";
import UserApiTokenFinder from "../../user/application/api-token/user-api-token-finder";
import {
	api_url as default_backend_api_url,
	store_entity_names
} from "../domain/config";

import RestError from "../domain/rest-error";
import BackendError from "../domain/errors/backend-error";
import RestAuthenticationError from "../../../libs/domain/classes/errors/rest-authentication-error";
import AppError from "../domain/errors/app-error";

export interface RequestOptions {
	body_params?: object;
	query_params?: {
		[key: string]: any; //Properties not explicitly defined
		offset?: number; //Defined optional property
	};
	files?: object;
	expect_file_response?: boolean;
	token?: string | null;
}

export default class BackendInterface {
	protected rest_interface: RestInterface;
	protected backend_url: string | null;
	protected user_api_token_finder: UserApiTokenFinder;
	protected store_interface: StoreInterface;
	protected user_token_entity_name: string;

	constructor(
		rest_interface?: RestInterface,
		backend_api_url?: string,
		user_api_token_finder?: UserApiTokenFinder,
		store_interface?: StoreInterface
	) {
		this.rest_interface = rest_interface || new RestInterface();
		this.backend_url = backend_api_url || default_backend_api_url;
		this.user_api_token_finder =
			user_api_token_finder || new UserApiTokenFinder();
		this.store_interface = store_interface || new StoreInterface();
		this.user_token_entity_name = store_entity_names.user_token;
	}

	public async get(
		url: string,
		args: RequestOptions | null = null
	): Promise<any> {
		return await this._rest_method("get", url, args);
	}

	public async post(
		url: string,
		args: RequestOptions | null = null
	): Promise<any> {
		return await this._rest_method("post", url, args);
	}

	public async put(
		url: string,
		args: RequestOptions | null = null
	): Promise<any> {
		return await this._rest_method("put", url, args);
	}

	public async delete(
		url: string,
		args: RequestOptions | null = null
	): Promise<any> {
		return await this._rest_method("delete", url, args);
	}

	private async _rest_method(
		method_name: "get" | "post" | "put" | "delete",
		url: string,
		args: RequestOptions | null = null
	): Promise<any> {
		const params: any = {};

		if (args?.body_params) params.body_params = args.body_params;
		if (args?.query_params) params.query_params = args.query_params;
		if (args?.files) params.files = args.files;
		if (args?.expect_file_response) params.expect_file_response = true;

		const user_token = args?.token ? args.token : this._get_user_token();
		if (user_token) {
			params.headers = { TOKEN: user_token };
		} else {
			const api_token = this._get_api_token();
			if (api_token) {
				params.headers = { "X-AUTH-TOKEN": api_token };
			}
		}

		try {
			const full_url = this.backend_url + url;

			const rest_interface_params =
				Object.keys(params).length > 0 ? [params] : [];

			return await (this.rest_interface as any)[method_name](
				full_url,
				...rest_interface_params
			);
		} catch (error: any) {
			const reported_error = this._check_rest_error(error);
			throw reported_error;
		}
	}

	private _get_user_token(): string | null | undefined {
		return this.store_interface.get(this.user_token_entity_name);
	}

	private _get_api_token(): string | null {
		return this.user_api_token_finder.get_user_api_token();
	}

	private _check_rest_error(
		rest_error: any
	): Error | BackendError | RestAuthenticationError | RestError {
		const response = rest_error.response;

		if (response?.data?.requestId && response?.data?.error?.code) {
			return new BackendError(response.data.error.code);
		} else if (response?.status) {
			if (response.status === 401) {
				return new RestAuthenticationError(
					response.status,
					response.message || undefined
				);
			} else {
				const message = response.message || response.data?.error || null;
				return new RestError(response.status, message || undefined);
			}
		}
		return new Error("unknown", rest_error);
	}

	/**
	 * Utility to fetch all items using pagination (offset-based)
	 */
	public async get_items_with_offset(
		url: string,
		multiple_async_calls: number | false = false,
		rest_params: RequestOptions | null = null
	): Promise<any[]> {
		const _get_results_with_offset = async (offset: number) => {
			const cloned_params: RequestOptions = rest_params
				? JSON.parse(JSON.stringify(rest_params))
				: { query_params: {} };

			if (!cloned_params.query_params) cloned_params.query_params = {};
			cloned_params.query_params.offset = offset;

			return await this.get(url, cloned_params);
		};

		let backend_items: any[] = [];

		if (multiple_async_calls) {
			const first_page = await _get_results_with_offset(0);
			backend_items = backend_items.concat(first_page.data);

			if (!first_page.offsetNext) return backend_items;

			const items_per_page = first_page.offsetNext;
			const total_items = first_page.totalElements;
			const total_pages = Math.ceil(total_items / items_per_page);

			const backend_pagination_data: Record<number, any[]> = {};
			let next_page = 2;

			const _get_next_page_data = async (): Promise<void> => {
				if (!next_page || next_page > total_pages) return;

				const current_page = next_page;
				const offset = (current_page - 1) * items_per_page;
				next_page++;

				const page_data = await _get_results_with_offset(offset);
				backend_pagination_data[current_page] = page_data.data;

				return _get_next_page_data();
			};

			const promises = [];
			for (let i = 0; i < (multiple_async_calls as number); i++) {
				promises.push(_get_next_page_data());
			}
			await Promise.all(promises);

			const sorted_keys = Object.keys(backend_pagination_data)
				.map(Number)
				.sort((a, b) => a - b);
			for (const key of sorted_keys) {
				backend_items = backend_items.concat(backend_pagination_data[key]);
			}
		} else {
			let next_offset: number | null = 0;
			while (next_offset !== null) {
				const result = await _get_results_with_offset(next_offset);
				backend_items = backend_items.concat(result.data);
				next_offset = result.offsetNext;
			}
		}
		return backend_items;
	}
}
